/**
 * SD Local Collective — Guided Inquiry state machine.
 *
 * Reads its questions from window.SDLocalGuidedInquiryData (see
 * guided-inquiry-data.js). This file only knows how to walk screens,
 * validate them, remember answers, and assemble the final message —
 * it does not hardcode any category/question copy.
 *
 * Submission is a standard, non-AJAX HTML form POST to Shopify's native
 * `contact` form action (the same mechanism sections/contact-form.liquid
 * already uses on this theme), for maximum reliability/compatibility.
 * The confirmation state is rendered server-side by Liquid after the
 * page reloads (see sections/guided-inquiry.liquid) — this script does
 * not render its own confirmation screen.
 */
(function () {
  'use strict';

  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  function formatDateLong(date) {
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  function todayAtMidnight() {
    var now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }

  function isoDateInputMin() {
    var d = todayAtMidnight();
    var mm = String(d.getMonth() + 1).padStart(2, '0');
    var dd = String(d.getDate()).padStart(2, '0');
    return d.getFullYear() + '-' + mm + '-' + dd;
  }

  function parseIsoDateLocal(iso) {
    // Parses "YYYY-MM-DD" as a local date (avoids UTC off-by-one).
    var parts = iso.split('-').map(Number);
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }

  var GuidedInquiryFlow = /** @class */ (function () {
    function GuidedInquiryFlow(root) {
      this.root = root;
      this.data = window.SDLocalGuidedInquiryData;
      this.viewport = root.querySelector('[data-guided-inquiry-viewport]');
      this.progressWrap = root.querySelector('[data-guided-inquiry-progress]');
      this.progressBar = root.querySelector('[data-guided-inquiry-progress-bar]');
      this.progressLabel = root.querySelector('[data-guided-inquiry-progress-label]');
      this.backButton = root.querySelector('[data-guided-inquiry-back]');
      this.nativeForm = root.closest('form');
      this.fieldName = this.nativeForm ? this.nativeForm.querySelector('[data-field="name"]') : null;
      this.fieldEmail = this.nativeForm ? this.nativeForm.querySelector('[data-field="email"]') : null;
      this.fieldPhone = this.nativeForm ? this.nativeForm.querySelector('[data-field="phone"]') : null;
      this.fieldBody = this.nativeForm ? this.nativeForm.querySelector('[data-field="body"]') : null;

      this.categoryId = null;
      this.screens = []; // ordered list of screen descriptors for the active path
      this.screenIndex = -1; // -1 = intro/category screen
      this.answers = {}; // questionId -> { value, label, otherText, isoDate }
      this.contact = { name: '', phone: '', email: '', preferredContact: '', notes: '', crossSell: [] };
      this.history = []; // stack of previous screenIndex values for Back

      this.backButton.addEventListener('click', this.goBack.bind(this));
      this.renderIntro();
    }

    GuidedInquiryFlow.prototype.categories = function () {
      return this.data.categories;
    };

    GuidedInquiryFlow.prototype.categoryById = function (id) {
      return this.categories().filter(function (c) { return c.id === id; })[0];
    };

    GuidedInquiryFlow.prototype.otherCategories = function () {
      var current = this.categoryId;
      return this.categories().filter(function (c) { return c.id !== current; });
    };

    // ---- Progress -------------------------------------------------------

    GuidedInquiryFlow.prototype.updateProgress = function () {
      if (this.screenIndex < 0) {
        this.progressWrap.hidden = true;
        this.backButton.hidden = true;
        return;
      }
      this.progressWrap.hidden = false;
      this.backButton.hidden = false;
      var total = this.screens.length;
      var current = this.screenIndex + 1;
      var pct = Math.round((current / total) * 100);
      this.progressBar.style.width = pct + '%';
      this.progressLabel.textContent = 'Step ' + current + ' of ' + total;
    };

    // ---- Navigation -------------------------------------------------------

    GuidedInquiryFlow.prototype.goBack = function () {
      if (!this.history.length) return;
      this.screenIndex = this.history.pop();
      if (this.screenIndex === -1) {
        this.renderIntro();
      } else {
        this.renderScreen();
      }
    };

    GuidedInquiryFlow.prototype.advanceTo = function (nextIndex) {
      this.history.push(this.screenIndex);
      this.screenIndex = nextIndex;
      this.renderScreen();
    };

    GuidedInquiryFlow.prototype.selectCategory = function (categoryId) {
      var category = this.categoryById(categoryId);
      this.categoryId = categoryId;
      this.screens = (category.questions || []).map(function (q) {
        return { kind: 'question', question: q };
      });
      this.screens.push({ kind: 'contact' });
      this.history.push(-1);
      this.screenIndex = 0;
      this.renderScreen();
    };

    // ---- Rendering: intro -------------------------------------------------

    GuidedInquiryFlow.prototype.renderIntro = function () {
      this.screenIndex = -1;
      this.updateProgress();
      var self = this;
      var cards = this.categories().map(function (c) {
        return (
          '<button type="button" class="guided-inquiry__card" data-category="' +
          c.id +
          '">' +
          escapeHtml(c.label) +
          '</button>'
        );
      }).join('');

      this.viewport.innerHTML =
        '<div class="guided-inquiry__intro">' +
        '<h2 class="guided-inquiry__headline">What can we help you with?</h2>' +
        '<p class="guided-inquiry__subhead">Tell us what you’re planning and we’ll help you figure out the next step.</p>' +
        '<p class="guided-inquiry__reassurance">Not sure exactly what you need? That’s okay — answer a few quick questions and we’ll help from there.</p>' +
        '<div class="guided-inquiry__cards" role="group" aria-label="Choose an inquiry type">' + cards + '</div>' +
        '</div>';

      this.viewport.querySelectorAll('[data-category]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var category = self.categoryById(btn.getAttribute('data-category'));
          if (category.isSimple) {
            self.categoryId = category.id;
            self.screens = [{ kind: 'contact' }];
            self.history.push(-1);
            self.screenIndex = 0;
            self.renderScreen();
          } else {
            self.selectCategory(category.id);
          }
        });
      });

      this.viewport.querySelector('.guided-inquiry__headline').focus({ preventScroll: false });
    };

    // ---- Rendering: dispatch -----------------------------------------------

    GuidedInquiryFlow.prototype.renderScreen = function () {
      this.updateProgress();
      var screen = this.screens[this.screenIndex];
      if (screen.kind === 'question') {
        this.renderQuestion(screen.question);
      } else {
        this.renderContactScreen();
      }
    };

    // ---- Rendering: question screens ---------------------------------------

    GuidedInquiryFlow.prototype.renderQuestion = function (question) {
      var self = this;
      var existing = this.answers[question.id];
      var html = '<div class="guided-inquiry__question">' +
        '<h2 class="guided-inquiry__question-label">' + escapeHtml(question.label) + '</h2>';

      if (question.type === 'single' || question.type === 'date-choice') {
        html += '<div class="guided-inquiry__options" role="group" aria-label="' + escapeHtml(question.label) + '">';
        question.options.forEach(function (opt) {
          var selected = existing && existing.value === opt.value;
          html += '<button type="button" class="guided-inquiry__option' + (selected ? ' is-selected' : '') +
            '" data-option="' + opt.value + '">' + escapeHtml(opt.label) + '</button>';
        });
        html += '</div>';
        html += '<div class="guided-inquiry__reveal" data-reveal hidden></div>';
        html += '<button type="button" class="guided-inquiry__continue" data-continue hidden>Continue</button>';
      } else if (question.type === 'multi') {
        html += '<div class="guided-inquiry__options guided-inquiry__options--multi" role="group" aria-label="' + escapeHtml(question.label) + '">';
        question.options.forEach(function (opt) {
          var selected = existing && existing.values && existing.values.indexOf(opt.value) !== -1;
          html += '<button type="button" class="guided-inquiry__chip' + (selected ? ' is-selected' : '') +
            '" data-option="' + opt.value + '" aria-pressed="' + (selected ? 'true' : 'false') + '">' +
            escapeHtml(opt.label) + '</button>';
        });
        html += '</div>';
        html += '<div class="guided-inquiry__reveal" data-reveal hidden></div>';
        html += '<button type="button" class="guided-inquiry__continue" data-continue>Continue</button>';
      } else if (question.type === 'text') {
        html += '<input type="text" class="guided-inquiry__text-input" data-text-input placeholder="' +
          escapeHtml(question.placeholder || '') + '" value="' + escapeHtml(existing ? existing.value : '') + '">' +
          '<button type="button" class="guided-inquiry__continue" data-continue>Continue</button>';
      }

      html += '</div>';
      this.viewport.innerHTML = html;
      this.wireQuestion(question);
      this.viewport.querySelector('.guided-inquiry__question-label').focus({ preventScroll: false });
    };

    GuidedInquiryFlow.prototype.wireQuestion = function (question) {
      var self = this;
      var continueBtn = this.viewport.querySelector('[data-continue]');
      var reveal = this.viewport.querySelector('[data-reveal]');

      function showOtherField(prefill) {
        reveal.hidden = false;
        reveal.innerHTML = '<label class="guided-inquiry__reveal-label">Please tell us more' +
          '<input type="text" class="guided-inquiry__text-input" data-other-input value="' + escapeHtml(prefill || '') + '"></label>';
        continueBtn.hidden = false;
        reveal.querySelector('[data-other-input]').focus();
      }

      function showDatePicker(prefill) {
        reveal.hidden = false;
        reveal.innerHTML = '<label class="guided-inquiry__reveal-label">Choose a date' +
          '<input type="date" class="guided-inquiry__date-input" data-date-input min="' + isoDateInputMin() +
          '" value="' + escapeHtml(prefill || '') + '"></label>';
        continueBtn.hidden = false;
        reveal.querySelector('[data-date-input]').focus();
      }

      if (question.type === 'single' || question.type === 'date-choice') {
        var options = this.viewport.querySelectorAll('[data-option]');
        var existing = this.answers[question.id];

        // Restore any previously revealed Other/date field when navigating back.
        if (existing && existing.needsOther) showOtherField(existing.otherText);
        if (existing && existing.needsDate) showDatePicker(existing.isoDate);

        options.forEach(function (btn) {
          btn.addEventListener('click', function () {
            options.forEach(function (b) { b.classList.remove('is-selected'); });
            btn.classList.add('is-selected');
            var opt = question.options.filter(function (o) { return o.value === btn.getAttribute('data-option'); })[0];

            if (opt.allowOther) {
              showOtherField('');
              continueBtn.onclick = function () {
                var text = reveal.querySelector('[data-other-input]').value.trim();
                if (!text) { reveal.querySelector('[data-other-input]').focus(); return; }
                self.answers[question.id] = { value: opt.value, label: opt.label, otherText: text, needsOther: true };
                self.goNext();
              };
              return;
            }

            if (opt.isDate) {
              showDatePicker('');
              continueBtn.onclick = function () {
                var input = reveal.querySelector('[data-date-input]');
                if (!input.value) { input.focus(); return; }
                var chosen = parseIsoDateLocal(input.value);
                if (chosen < todayAtMidnight()) { input.focus(); return; }
                self.answers[question.id] = {
                  value: opt.value, label: opt.label, needsDate: true,
                  isoDate: input.value, resolvedText: opt.label + ' — ' + formatDateLong(chosen)
                };
                self.goNext();
              };
              return;
            }

            // Plain option (including relative "today"/"tomorrow" and non-resolvable labels).
            reveal.hidden = true;
            reveal.innerHTML = '';
            var answer = { value: opt.value, label: opt.label };
            if (opt.relative === 'today' || opt.relative === 'tomorrow') {
              var d = todayAtMidnight();
              if (opt.relative === 'tomorrow') d.setDate(d.getDate() + 1);
              answer.resolvedText = opt.label + ' — ' + formatDateLong(d);
            }
            self.answers[question.id] = answer;
            self.goNext();
          });
        });
      }

      if (question.type === 'multi') {
        var chips = this.viewport.querySelectorAll('[data-option]');
        var existingMulti = (this.answers[question.id] && this.answers[question.id].values) || [];
        var selectedValues = existingMulti.slice();
        var otherChip = question.options.filter(function (o) { return o.allowOther; })[0];
        var existingOtherText = this.answers[question.id] ? this.answers[question.id].otherText : '';

        if (otherChip && selectedValues.indexOf(otherChip.value) !== -1) showOtherField(existingOtherText);

        chips.forEach(function (btn) {
          btn.addEventListener('click', function () {
            var val = btn.getAttribute('data-option');
            var idx = selectedValues.indexOf(val);
            if (idx === -1) {
              selectedValues.push(val);
              btn.classList.add('is-selected');
              btn.setAttribute('aria-pressed', 'true');
            } else {
              selectedValues.splice(idx, 1);
              btn.classList.remove('is-selected');
              btn.setAttribute('aria-pressed', 'false');
            }
            if (otherChip) {
              if (selectedValues.indexOf(otherChip.value) !== -1) showOtherField(existingOtherText);
              else { reveal.hidden = true; reveal.innerHTML = ''; }
            }
          });
        });

        continueBtn.addEventListener('click', function () {
          if ((question.minSelections || 0) > 0 && selectedValues.length < question.minSelections) return;
          var otherText = '';
          if (otherChip && selectedValues.indexOf(otherChip.value) !== -1) {
            otherText = reveal.querySelector('[data-other-input]') ? reveal.querySelector('[data-other-input]').value.trim() : '';
            if (!otherText) { reveal.querySelector('[data-other-input]').focus(); return; }
          }
          var labels = selectedValues.map(function (v) {
            var o = question.options.filter(function (opt) { return opt.value === v; })[0];
            return (o.allowOther && otherText) ? otherText : o.label;
          });
          self.answers[question.id] = { values: selectedValues, labels: labels, otherText: otherText };
          self.goNext();
        });
      }

      if (question.type === 'text') {
        var input = this.viewport.querySelector('[data-text-input]');
        continueBtn.addEventListener('click', function () {
          var val = input.value.trim();
          if (question.required && !val) { input.focus(); return; }
          self.answers[question.id] = { value: val, label: val };
          self.goNext();
        });
      }
    };

    GuidedInquiryFlow.prototype.goNext = function () {
      if (this.screenIndex + 1 < this.screens.length) {
        this.advanceTo(this.screenIndex + 1);
      }
    };

    // ---- Rendering: shared contact screen ----------------------------------

    GuidedInquiryFlow.prototype.renderContactScreen = function () {
      var self = this;
      var category = this.categoryById(this.categoryId);
      var isGeneral = !!category.isSimple;
      var overrides = isGeneral ? this.data.generalQuestionOverrides : {};
      var notesLabel = overrides.notesLabel || 'Optional notes';
      var notesRequired = !!overrides.notesRequired;
      var hideCrossSell = !!overrides.hideCrossSell;
      var c = this.contact;

      var contactOptions = this.data.contactScreen.fields.preferredContact.options.map(function (opt) {
        var selected = c.preferredContact === opt.value;
        return '<button type="button" class="guided-inquiry__option' + (selected ? ' is-selected' : '') +
          '" data-contact-option="' + opt.value + '">' + escapeHtml(opt.label) + '</button>';
      }).join('');

      var crossSellHtml = '';
      if (!hideCrossSell) {
        var chips = this.otherCategories().map(function (cat) {
          var checked = c.crossSell.indexOf(cat.id) !== -1;
          return '<button type="button" class="guided-inquiry__chip' + (checked ? ' is-selected' : '') +
            '" data-cross-sell="' + cat.id + '" aria-pressed="' + (checked ? 'true' : 'false') + '">' +
            escapeHtml(cat.label) + '</button>';
        }).join('');
        crossSellHtml =
          '<div class="guided-inquiry__field">' +
          '<span class="guided-inquiry__field-label">Anything else we can help with? <span class="guided-inquiry__optional">(optional)</span></span>' +
          '<div class="guided-inquiry__options guided-inquiry__options--multi">' + chips + '</div>' +
          '</div>';
      }

      this.viewport.innerHTML =
        '<div class="guided-inquiry__question guided-inquiry__contact">' +
        '<h2 class="guided-inquiry__question-label">Almost done — how should we reach you?</h2>' +
        '<label class="guided-inquiry__field">Name<input type="text" class="guided-inquiry__text-input" data-contact-name value="' + escapeHtml(c.name) + '"></label>' +
        '<label class="guided-inquiry__field">Email<input type="email" class="guided-inquiry__text-input" data-contact-email value="' + escapeHtml(c.email) + '"></label>' +
        '<label class="guided-inquiry__field">Phone <span class="guided-inquiry__optional" data-phone-optional-tag></span><input type="tel" class="guided-inquiry__text-input" data-contact-phone value="' + escapeHtml(c.phone) + '"></label>' +
        '<div class="guided-inquiry__field"><span class="guided-inquiry__field-label">Preferred contact method</span>' +
        '<div class="guided-inquiry__options">' + contactOptions + '</div></div>' +
        '<label class="guided-inquiry__field">' + escapeHtml(notesLabel) + (notesRequired ? '' : ' <span class="guided-inquiry__optional">(optional)</span>') +
        '<textarea class="guided-inquiry__textarea" data-contact-notes rows="4">' + escapeHtml(c.notes) + '</textarea></label>' +
        crossSellHtml +
        '<p class="guided-inquiry__error" data-contact-error hidden></p>' +
        '<button type="button" class="guided-inquiry__submit" data-contact-submit>Submit Inquiry</button>' +
        '</div>';

      var phoneTag = this.viewport.querySelector('[data-phone-optional-tag]');
      function refreshPhoneRequirement() {
        var needsPhone = c.preferredContact === 'text' || c.preferredContact === 'phone';
        phoneTag.textContent = needsPhone ? '' : '(optional)';
      }
      refreshPhoneRequirement();

      this.viewport.querySelectorAll('[data-contact-option]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.viewport.querySelectorAll('[data-contact-option]').forEach(function (b) { b.classList.remove('is-selected'); });
          btn.classList.add('is-selected');
          c.preferredContact = btn.getAttribute('data-contact-option');
          refreshPhoneRequirement();
        });
      });

      if (!hideCrossSell) {
        this.viewport.querySelectorAll('[data-cross-sell]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var id = btn.getAttribute('data-cross-sell');
            var idx = c.crossSell.indexOf(id);
            if (idx === -1) { c.crossSell.push(id); btn.classList.add('is-selected'); btn.setAttribute('aria-pressed', 'true'); }
            else { c.crossSell.splice(idx, 1); btn.classList.remove('is-selected'); btn.setAttribute('aria-pressed', 'false'); }
          });
        });
      }

      this.viewport.querySelector('[data-contact-submit]').addEventListener('click', function () {
        self.submit(isGeneral, notesRequired);
      });
    };

    // ---- Submission ---------------------------------------------------------

    GuidedInquiryFlow.prototype.submit = function (isGeneral, notesRequired) {
      var c = this.contact;
      var errorEl = this.viewport.querySelector('[data-contact-error]');
      c.name = this.viewport.querySelector('[data-contact-name]').value.trim();
      c.email = this.viewport.querySelector('[data-contact-email]').value.trim();
      c.phone = this.viewport.querySelector('[data-contact-phone]').value.trim();
      c.notes = this.viewport.querySelector('[data-contact-notes]').value.trim();

      var errors = [];
      if (c.name.length < 2) errors.push('Please enter your name.');
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email)) errors.push('Please enter a valid email address.');
      if (!c.preferredContact) errors.push('Please choose a preferred contact method.');
      if ((c.preferredContact === 'text' || c.preferredContact === 'phone') && !c.phone) errors.push('Please enter a phone number.');
      if (notesRequired && !c.notes) errors.push("Please let us know what your question is.");

      if (errors.length) {
        errorEl.hidden = false;
        errorEl.textContent = errors.join(' ');
        return;
      }
      errorEl.hidden = true;

      var category = this.categoryById(this.categoryId);
      var lines = [];
      lines.push('NEW SD LOCAL INQUIRY');
      lines.push('Service: ' + category.label);
      lines.push('Customer: ' + c.name);
      lines.push('Phone: ' + (c.phone || '—'));
      lines.push('Email: ' + c.email);
      lines.push('Preferred Contact: ' + this.contactMethodLabel(c.preferredContact));
      lines.push('Submitted: ' + new Date().toLocaleString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit'
      }));
      lines.push('');
      lines.push('— Details —');

      if (isGeneral) {
        lines.push(this.data.generalQuestionOverrides.notesLabel + ': ' + c.notes);
        lines.push('');
        lines.push('Notes: —');
      } else {
        category.questions.forEach(function (q) {
          var a = this.answers[q.id];
          if (!a) return;
          var text;
          if (a.values) text = a.labels.join(', ');
          else if (a.needsOther) text = a.otherText;
          else if (a.resolvedText) text = a.resolvedText;
          else text = a.label;
          lines.push(q.label.replace(/[?]$/, '') + ': ' + text);
        }, this);
        lines.push('');
        lines.push('Notes: ' + (c.notes || '—'));
      }

      lines.push('');
      var crossSellLabels = c.crossSell.map(function (id) { return this.categoryById(id).label; }, this);
      lines.push('Also interested in: ' + (crossSellLabels.length ? crossSellLabels.join(', ') : '—'));

      // Defensive check: the native form and its hidden Shopify contact fields
      // must all resolve before we populate/submit. If any are missing (e.g. a
      // future markup change breaks the lookup again), fail loud — a visible
      // message plus a console error — instead of silently doing nothing.
      if (!this.nativeForm || !this.fieldName || !this.fieldEmail || !this.fieldPhone || !this.fieldBody) {
        errorEl.hidden = false;
        errorEl.textContent = "We couldn't submit your inquiry. Please try again, or contact SD Local directly.";
        console.error('[SD Local Guided Inquiry] Cannot submit — native form or a hidden contact field was not found.', {
          nativeForm: this.nativeForm,
          fieldName: this.fieldName,
          fieldEmail: this.fieldEmail,
          fieldPhone: this.fieldPhone,
          fieldBody: this.fieldBody
        });
        return;
      }

      this.fieldName.value = c.name;
      this.fieldEmail.value = c.email;
      this.fieldPhone.value = c.phone;
      this.fieldBody.value = lines.join('\n');

      // Standard, non-AJAX HTML form submission — a full page POST/reload,
      // matching the reliability of the theme's existing native contact form.
      this.nativeForm.submit();
    };

    GuidedInquiryFlow.prototype.contactMethodLabel = function (value) {
      var opt = this.data.contactScreen.fields.preferredContact.options.filter(function (o) { return o.value === value; })[0];
      return opt ? opt.label : value;
    };

    return GuidedInquiryFlow;
  })();

  class GuidedInquiryElement extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('data-posted-successfully')) return; // Liquid already rendered the confirmation state.
      if (!window.SDLocalGuidedInquiryData) return;
      new GuidedInquiryFlow(this);
    }
  }

  if (!customElements.get('guided-inquiry-flow')) {
    customElements.define('guided-inquiry-flow', GuidedInquiryElement);
  }
})();
